import React from 'react'
import { Alert, Box, Skeleton, Stack, TablePagination, Typography } from '@mui/material'
import GanttToolbar from './components/GanttToolbar'
import GanttLegacyBanner from './components/GanttLegacyBanner'
import {
  GanttTaskListCell,
  GanttTaskListHeader,
  LEFT_COLUMN_WIDTH,
  ROW_HEIGHT,
} from './components/GanttTaskList'
import {
  GanttTimelineCell,
  GanttTimelineHeader,
  GanttTimelineLegend,
  getColumnWidth,
} from './components/GanttTimeline'
import LegacyTasksModal from './components/LegacyTasksModal'
import {
  buildTimelineColumns,
  GANTT_VIEW_MODE,
  getVisibleRangeLabel,
  shiftAnchorDate,
  toSafeDate,
} from './helpers/ganttDates'
import { filterProjectTree, flattenVisibleRows } from './helpers/ganttFilters'
import { buildGanttProjectTree } from './helpers/ganttMapper'

const STORAGE_KEY = 'task-management-gantt-view-mode'
const STATUS_FILTER_STORAGE_KEY = 'task-management-gantt-project-status'
const TIMELINE_GUTTER_WIDTH = 8
const TIMELINE_SCROLLBAR_LANE_WIDTH = 14
const TIMELINE_RIGHT_SIDE_WIDTH = TIMELINE_GUTTER_WIDTH + TIMELINE_SCROLLBAR_LANE_WIDTH
const PROJECTS_PER_PAGE = 5

export default function TrackerGanttChart({
  projects = [],
  tasks = [],
  currentStaffId = null,
  loading = false,
  onTaskUpdated,
}) {
  const [viewMode, setViewMode] = React.useState(() => localStorage.getItem(STORAGE_KEY) || GANTT_VIEW_MODE.MONTH)
  const [projectStatus, setProjectStatus] = React.useState(
    () => localStorage.getItem(STATUS_FILTER_STORAGE_KEY) || 'ongoing'
  )
  const [anchorDate, setAnchorDate] = React.useState(() => new Date())
  const [searchInput, setSearchInput] = React.useState('')
  const [searchQuery, setSearchQuery] = React.useState('')
  const [expandedProjectIds, setExpandedProjectIds] = React.useState(new Set())
  const [legacyModalOpen, setLegacyModalOpen] = React.useState(false)
  const [isHorizontalScrollActive, setIsHorizontalScrollActive] = React.useState(false)
  const [page, setPage] = React.useState(1)
  const headerTimelineRef = React.useRef(null)
  const bodyTimelineRef = React.useRef(null)
  const verticalViewportRef = React.useRef(null)
  const scrollbarViewportRef = React.useRef(null)
  const isSyncingHorizontalScrollRef = React.useRef(false)
  const isSyncingVerticalScrollRef = React.useRef(false)
  const horizontalScrollTimeoutRef = React.useRef(null)

  React.useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setSearchQuery(searchInput.trim())
    }, 300)

    return () => window.clearTimeout(timeoutId)
  }, [searchInput])

  React.useEffect(() => {
    localStorage.setItem(STORAGE_KEY, viewMode)
  }, [viewMode])

  React.useEffect(() => {
    localStorage.setItem(STATUS_FILTER_STORAGE_KEY, projectStatus)
  }, [projectStatus])

  const getLegacyTaskOwnerId = (task) => {
    const assignedTo = task?.assigned_to

    if (assignedTo && typeof assignedTo === 'object') {
      return assignedTo?.staff_id ?? assignedTo?.id ?? null
    }

    return assignedTo ?? task?.staff_id ?? task?.assignee_id ?? null
  }

  const { projectTree, legacyTasks } = React.useMemo(
    () => buildGanttProjectTree({ projects, tasks }),
    [projects, tasks]
  )

  const myLegacyTasks = React.useMemo(() => {
    if (!currentStaffId) return []

    return legacyTasks.filter((task) => {
      const ownerId = getLegacyTaskOwnerId(task)
      return ownerId != null && String(ownerId) === String(currentStaffId)
    })
  }, [currentStaffId, legacyTasks])

  const statusFilteredProjects = React.useMemo(() => {
    if (projectStatus === 'all') return projectTree
    return projectTree.filter((project) => project.normalizedProjectStatus === projectStatus)
  }, [projectStatus, projectTree])

  const filteredProjects = React.useMemo(
    () => filterProjectTree(statusFilteredProjects, searchQuery),
    [statusFilteredProjects, searchQuery]
  )
  const totalProjectPages = React.useMemo(
    () => Math.max(1, Math.ceil(filteredProjects.length / PROJECTS_PER_PAGE)),
    [filteredProjects.length]
  )
  const paginatedProjects = React.useMemo(() => {
    const start = (page - 1) * PROJECTS_PER_PAGE
    return filteredProjects.slice(start, start + PROJECTS_PER_PAGE)
  }, [filteredProjects, page])

  React.useEffect(() => {
    setPage(1)
  }, [searchQuery, projectStatus])

  React.useEffect(() => {
    if (page > totalProjectPages) {
      setPage(totalProjectPages)
    }
  }, [page, totalProjectPages])

  React.useEffect(() => {
    setExpandedProjectIds((previous) => {
      if (searchQuery) {
        return new Set(paginatedProjects.map((project) => project.id))
      }
      if (previous.size) return previous
      return new Set(paginatedProjects.map((project) => project.id))
    })
  }, [paginatedProjects, searchQuery])

  const rows = React.useMemo(
    () => flattenVisibleRows(paginatedProjects, expandedProjectIds, searchQuery),
    [expandedProjectIds, paginatedProjects, searchQuery]
  )

  const { columns, rangeStart, rangeEnd } = React.useMemo(
    () => buildTimelineColumns(anchorDate, viewMode),
    [anchorDate, viewMode]
  )
  const columnWidth = React.useMemo(() => getColumnWidth(viewMode), [viewMode])
  const timelineWidth = React.useMemo(() => columns.length * columnWidth, [columnWidth, columns.length])

  const handleExpandAll = React.useCallback(() => {
    setExpandedProjectIds((previous) => {
      const next = new Set(previous)
      paginatedProjects.forEach((project) => next.add(project.id))
      return next
    })
  }, [paginatedProjects])

  const handleCollapseAll = React.useCallback(() => {
    setExpandedProjectIds((previous) => {
      const next = new Set(previous)
      paginatedProjects.forEach((project) => next.delete(project.id))
      return next
    })
  }, [paginatedProjects])

  const handleToggleProject = React.useCallback((projectId) => {
    setExpandedProjectIds((previous) => {
      const next = new Set(previous)
      if (next.has(projectId)) next.delete(projectId)
      else next.add(projectId)
      return next
    })
  }, [])

  const markHorizontalScrollActive = React.useCallback(() => {
    setIsHorizontalScrollActive(true)

    if (horizontalScrollTimeoutRef.current) {
      window.clearTimeout(horizontalScrollTimeoutRef.current)
    }

    horizontalScrollTimeoutRef.current = window.setTimeout(() => {
      setIsHorizontalScrollActive(false)
      horizontalScrollTimeoutRef.current = null
    }, 900)
  }, [])

  const syncHorizontalScroll = React.useCallback((sourceRef, targetRef) => {
    if (isSyncingHorizontalScrollRef.current) return
    if (!sourceRef.current || !targetRef.current) return

    markHorizontalScrollActive()
    isSyncingHorizontalScrollRef.current = true
    targetRef.current.scrollLeft = sourceRef.current.scrollLeft

    window.requestAnimationFrame(() => {
      isSyncingHorizontalScrollRef.current = false
    })
  }, [markHorizontalScrollActive])

  const syncVerticalScroll = React.useCallback((sourceRef, targetRef) => {
    if (isSyncingVerticalScrollRef.current) return
    if (!sourceRef.current || !targetRef.current) return

    isSyncingVerticalScrollRef.current = true
    targetRef.current.scrollTop = sourceRef.current.scrollTop

    window.requestAnimationFrame(() => {
      isSyncingVerticalScrollRef.current = false
    })
  }, [])

  React.useEffect(() => {
    return () => {
      if (horizontalScrollTimeoutRef.current) {
        window.clearTimeout(horizontalScrollTimeoutRef.current)
      }
    }
  }, [])

  React.useEffect(() => {
    if (!searchQuery || !verticalViewportRef.current) return

    const firstTaskRow = rows.find((row) => row.rowType === 'task')
    if (!firstTaskRow) {
      verticalViewportRef.current.scrollTop = 0
      if (scrollbarViewportRef.current) scrollbarViewportRef.current.scrollTop = 0
      return
    }

    const matchingRowNode = verticalViewportRef.current.querySelector(
      `[data-row-id="${firstTaskRow.id}"]`
    )

    if (!matchingRowNode) return

    const nextScrollTop = Math.max(0, matchingRowNode.offsetTop - 16)
    verticalViewportRef.current.scrollTop = nextScrollTop
    if (scrollbarViewportRef.current) scrollbarViewportRef.current.scrollTop = nextScrollTop
  }, [rangeEnd, rangeStart, rows, searchQuery])

  React.useEffect(() => {
    if (!bodyTimelineRef.current) return
    if (!searchQuery) {
      bodyTimelineRef.current.scrollLeft = 0
      if (headerTimelineRef.current) headerTimelineRef.current.scrollLeft = 0
      return
    }

    const firstTaskRow = rows.find((row) => row.rowType === 'task')
    if (!firstTaskRow) {
      bodyTimelineRef.current.scrollLeft = 0
      if (headerTimelineRef.current) headerTimelineRef.current.scrollLeft = 0
      return
    }

    const firstTimelineItem = firstTaskRow?.timelineItems?.[0] || firstTaskRow?.timelineItem
    const taskStartDate = toSafeDate(firstTimelineItem?.start)
    const taskEndDate = toSafeDate(firstTimelineItem?.end)
    if (!taskStartDate || !taskEndDate) return

    if (taskEndDate < rangeStart || taskStartDate > rangeEnd) {
      bodyTimelineRef.current.scrollLeft = 0
      if (headerTimelineRef.current) headerTimelineRef.current.scrollLeft = 0
      return
    }

    const visibleStartDate = taskStartDate < rangeStart ? rangeStart : taskStartDate

    const startColumnIndex = columns.findIndex(
      (column) => column.date.getTime() === visibleStartDate.getTime()
    )
    if (startColumnIndex < 0) {
      bodyTimelineRef.current.scrollLeft = 0
      if (headerTimelineRef.current) headerTimelineRef.current.scrollLeft = 0
      return
    }

    const desiredOffset = Math.max(
      0,
      startColumnIndex * columnWidth - columnWidth * 0.5
    )
    bodyTimelineRef.current.scrollLeft = desiredOffset
    if (headerTimelineRef.current) headerTimelineRef.current.scrollLeft = desiredOffset
  }, [columnWidth, columns, rangeEnd, rangeStart, rows, searchQuery])

  if (loading) {
    return (
      <Stack spacing={2} sx={{ mt: 3 }}>
        <Skeleton variant="rounded" height={48} />
        <Skeleton variant="rounded" height={520} />
      </Stack>
    )
  }

  return (
    <Box sx={{ mt: 3, pb: 4 }}>
      <Stack spacing={2.5}>
        <GanttToolbar
          searchValue={searchInput}
          onSearchChange={setSearchInput}
          onClearSearch={() => {
            setSearchInput('')
            setSearchQuery('')
          }}
          projectStatus={projectStatus}
          onProjectStatusChange={setProjectStatus}
          onExpandAll={handleExpandAll}
          onCollapseAll={handleCollapseAll}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          visibleRangeLabel={getVisibleRangeLabel(anchorDate, viewMode)}
          onPrevRange={() => setAnchorDate((current) => shiftAnchorDate(current, viewMode, -1))}
          onNextRange={() => setAnchorDate((current) => shiftAnchorDate(current, viewMode, 1))}
        />

        <GanttLegacyBanner hiddenCount={myLegacyTasks.length} onReview={() => setLegacyModalOpen(true)} />

        {!rows.length ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            No Gantt rows available for the current filters.
          </Alert>
        ) : (
          <Box
            sx={{
              border: '1px solid #EAECF0',
              borderRadius: 2,
              overflow: 'hidden',
              backgroundColor: '#FFFFFF',
              boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
            }}
          >
            <Box sx={{ display: 'flex', borderBottom: '1px solid #D0D5DD', backgroundColor: '#FFFFFF' }}>
              <GanttTaskListHeader />
              <Box sx={{ flex: 1, minWidth: 0, display: 'flex', backgroundColor: '#FFFFFF' }}>
                <Box
                  ref={headerTimelineRef}
                  onScroll={() => syncHorizontalScroll(headerTimelineRef, bodyTimelineRef)}
                  className={isHorizontalScrollActive ? 'is-scrolling' : ''}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'transparent transparent',
                    '&::-webkit-scrollbar': {
                      height: '10px',
                    },
                    '&::-webkit-scrollbar-track': {
                      backgroundColor: 'transparent',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      backgroundColor: 'transparent',
                      borderRadius: '999px',
                      border: '2px solid transparent',
                      backgroundClip: 'padding-box',
                    },
                    '&:hover': {
                      scrollbarColor: 'rgba(16, 24, 40, 0.18) transparent',
                    },
                    '&:hover::-webkit-scrollbar-thumb': {
                      backgroundColor: 'rgba(16, 24, 40, 0.18)',
                    },
                    '&.is-scrolling': {
                      scrollbarColor: 'rgba(16, 24, 40, 0.18) transparent',
                    },
                    '&.is-scrolling::-webkit-scrollbar-thumb': {
                      backgroundColor: 'rgba(16, 24, 40, 0.18)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      minWidth: timelineWidth,
                    }}
                  >
                    <GanttTimelineHeader columns={columns} columnWidth={columnWidth} />
                  </Box>
                </Box>
                <Box
                  aria-hidden
                  sx={{
                    width: `${TIMELINE_RIGHT_SIDE_WIDTH}px`,
                    flexShrink: 0,
                    backgroundColor: '#FFFFFF',
                    borderBottom: '1px solid #D0D5DD',
                  }}
                />
              </Box>
            </Box>

            <Box
              sx={{
                height: 700,
                overflow: 'hidden',
                boxSizing: 'border-box',
                position: 'relative',
                display: 'flex',
              }}
            >
              <Box
                ref={verticalViewportRef}
                onScroll={() => syncVerticalScroll(verticalViewportRef, scrollbarViewportRef)}
                sx={{
                  height: '100%',
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                }}
              >
                <Box sx={{ display: 'flex', minWidth: LEFT_COLUMN_WIDTH }}>
                  <Box
                    sx={{
                      width: LEFT_COLUMN_WIDTH,
                      minWidth: LEFT_COLUMN_WIDTH,
                      flexShrink: 0,
                      backgroundColor: '#FFFFFF',
                    }}
                  >
                    {rows.map((row) => (
                      <Box key={`list-${row.id}`} data-row-id={row.id}>
                        <GanttTaskListCell
                          row={row}
                          expandedProjectIds={expandedProjectIds}
                          onToggleProject={handleToggleProject}
                        />
                      </Box>
                    ))}
                  </Box>

                  <Box
                    sx={{ flex: 1, minWidth: 0, display: 'flex', backgroundColor: '#FFFFFF' }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', backgroundColor: '#FFFFFF' }}>
                      <Box
                        ref={bodyTimelineRef}
                        onScroll={() => syncHorizontalScroll(bodyTimelineRef, headerTimelineRef)}
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          overflowX: 'auto',
                          overflowY: 'hidden',
                          scrollbarWidth: 'none',
                          msOverflowStyle: 'none',
                          '&::-webkit-scrollbar': {
                            display: 'none',
                          },
                        }}
                      >
                        <Box
                          sx={{
                            width: '100%',
                            minWidth: timelineWidth,
                          }}
                        >
                          {rows.map((row) => (
                            <Box
                              key={`timeline-${row.id}`}
                              sx={{
                                display: 'flex',
                                width: '100%',
                                height: ROW_HEIGHT + 1,
                                position: 'relative',
                                boxSizing: 'border-box',
                                '&::after': {
                                  content: '""',
                                  position: 'absolute',
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  height: '1px',
                                  backgroundColor: '#EAECF0',
                                  pointerEvents: 'none',
                                  zIndex: 4,
                                },
                              }}
                            >
                              <Box sx={{ width: timelineWidth, minWidth: timelineWidth, height: '100%' }}>
                                <GanttTimelineCell
                                  row={row}
                                  columns={columns}
                                  rangeStart={rangeStart}
                                  rangeEnd={rangeEnd}
                                  columnWidth={columnWidth}
                                  safeRightGutterWidth={TIMELINE_RIGHT_SIDE_WIDTH}
                                />
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>

                      <Box
                        aria-hidden
                        sx={{
                          width: `${TIMELINE_GUTTER_WIDTH}px`,
                          minWidth: `${TIMELINE_GUTTER_WIDTH}px`,
                          flexShrink: 0,
                          backgroundColor: '#FFFFFF',
                        }}
                      >
                        {rows.map((row) => (
                          <Box
                            key={`gutter-${row.id}`}
                            sx={{
                              width: '100%',
                              height: ROW_HEIGHT + 1,
                              boxSizing: 'border-box',
                              borderBottom: '1px solid #EAECF0',
                              backgroundColor: '#FFFFFF',
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Box
                ref={scrollbarViewportRef}
                onScroll={() => syncVerticalScroll(scrollbarViewportRef, verticalViewportRef)}
                sx={{
                  width: `${TIMELINE_SCROLLBAR_LANE_WIDTH}px`,
                  minWidth: `${TIMELINE_SCROLLBAR_LANE_WIDTH}px`,
                  flexShrink: 0,
                  height: '100%',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  backgroundColor: '#FFFFFF',
                  '&::-webkit-scrollbar': { width: '14px', height: '14px' },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(16, 24, 40, 0.18)',
                    borderRadius: '999px',
                    border: '3px solid transparent',
                    backgroundClip: 'padding-box',
                  },
                }}
              >
                <Box sx={{ width: '100%', minWidth: 0 }}>
                  {rows.map((row) => (
                    <Box
                      key={`scrollbar-lane-${row.id}`}
                      sx={{
                        width: '100%',
                        height: ROW_HEIGHT + 1,
                        boxSizing: 'border-box',
                        backgroundColor: '#FFFFFF',
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: 0,
                          // left: `${-TIMELINE_GUTTER_WIDTH + 6 }px`,
                          left: '-14px',
                          right: 0,
                          height: '1px',
                          backgroundColor: '#EAECF0',
                          pointerEvents: 'none',
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>

            <GanttTimelineLegend projectCount={filteredProjects.length} />
          </Box>
        )}

        <Typography variant="caption" sx={{ color: '#667085' }}>
          Search filters project groups and timeline bars together. Legacy tasks without a To-Do Date are excluded until updated.
        </Typography>
      </Stack>

      {filteredProjects.length > 0 && (
        <Box
          sx={{
            borderTop: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'flex-end',
            backgroundColor: '#ffffff',
            position: 'sticky',
            bottom: 0,
            zIndex: 10,
            mx: -3,
            mb: -4,
            px: 3,
          }}
        >
          <TablePagination
            component="div"
            count={filteredProjects.length}
            page={page - 1}
            onPageChange={(_, nextPage) => setPage(nextPage + 1)}
            rowsPerPage={PROJECTS_PER_PAGE}
            rowsPerPageOptions={[]}
            showFirstButton={true}
            showLastButton={true}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
            sx={{
              '& .MuiTablePagination-toolbar': {
                minHeight: '48px',
                paddingLeft: 0,
                paddingRight: 0,
              },
            }}
          />
        </Box>
      )}

      <LegacyTasksModal
        open={legacyModalOpen}
        onClose={() => {
          setLegacyModalOpen(false)
          onTaskUpdated?.()
        }}
        tasks={myLegacyTasks}
      />
    </Box>
  )
}
